import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits
} from '../../../../types';
import {
  FetchCacheTeamsFilters,
  FetchTeamsLimit,
  FetchTeamsPage,
  FetchTeamsSort
} from '../../teamsTypes';
import { InfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useInfiniteIndexQuery';

import {
  FETCH_CACHE_TEAMS_QUERY,
  FetchCacheTeamsQueryResponse
} from '../../queries/fetchCacheTeams.query';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_TEAMS_FILTERS,
  INITIAL_TEAMS_LIMIT,
  INITIAL_TEAMS_SORT
} from '../../teamsConstants';

interface CacheTeamsOptions {
  action?: string;
  cacheKey: string;
  query?: string;
  cacheQuery?: string;
  initialFilters?: FetchCacheTeamsFilters;
  initialSort?: FetchTeamsSort;
  initialPage?: FetchTeamsPage;
  initialLimit?: FetchTeamsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  source?: FetchCacheItemsSources;
}

const scope = 'teams';

function useCacheTeams<
  ItemType extends InfiniteIndexQueryBaseNodeType = FetchCacheTeamsQueryResponse
>({
  action,
  cacheKey,
  query = FETCH_CACHE_TEAMS_QUERY,
  cacheQuery,
  initialFilters = INITIAL_TEAMS_FILTERS,
  initialSort = INITIAL_TEAMS_SORT,
  initialLimit = INITIAL_TEAMS_LIMIT,
  options = {},
  trackTotalHits,
  source = FetchCacheItemsSources.DB
}: CacheTeamsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems
  } = useCacheInfiniteIndexQuery<ItemType>({
    action,
    defaultApi: 'cache_api',
    scope,
    query,
    cacheQuery: cacheQuery || query,
    cacheKey,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    trackTotalHits,
    source
  });

  return {
    teamsData: data,
    teams: items,
    teamsError: itemsError,
    teamsTotalCount: itemsTotalCount,
    teamsFetched: isFetched,
    teamsLoading: isLoading,
    teamsFetchingNextPage: isFetchingNextPage,
    teamsIsPlaceholderData: isPlaceholderData,
    teamsFilters: currentFilters,
    teamsSort: currentSort,
    teamsPage: currentPage,
    teamsLimit: currentLimit,
    teamsHasNextPage: hasNextPage,
    hasNextTeamsPage: hasNextPage,
    updateTeamCache: updateItemCache,
    loadMoreTeams: loadMoreItems,
    filterTeams: filterItems,
    changeTeamsFilters: changeItemsFilters,
    clearTeamsFilters: clearItemsFilters,
    clearTeamsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortTeams: sortItems,
    limitTeams: limitItems
  };
}

export default useCacheTeams;
